import type { SetupGuideResolver } from '../resolvers/types';
import type { Business } from '../../../types/business';
import { resolveBusinessProfileItem } from '../resolvers/businessProfileResolver';
import { resolveSetupWizardItem } from '../resolvers/setupWizardResolver';
import { resolveHoursItem } from '../resolvers/hoursResolver';
import { resolveServicesItem } from '../resolvers/servicesResolver';
import { resolveTaxItem } from '../resolvers/taxResolver';
import { resolveInvoiceBrandingItem } from '../resolvers/invoiceBrandingResolver';
import { resolveBookingPageItem } from '../resolvers/bookingPageResolver';
import { resolveIntroOffersItem } from '../resolvers/introOffersResolver';
import { resolveAddTeamItem } from '../resolvers/addTeamResolver';
import { resolveAddContractorsItem } from '../resolvers/addContractorsResolver';
import { resolveCommissionTrackingItem } from '../resolvers/commissionTrackingResolver';
import { resolveImportClientsItem } from '../resolvers/importClientsResolver';
import { resolveStripeConnectItem } from '../resolvers/stripeConnectResolver';
import { resolveSendFirstInvoiceItem } from '../resolvers/sendFirstInvoiceResolver';
import { resolveReceiveFirstPaymentItem } from '../resolvers/receiveFirstPaymentResolver';
import {
  hasContractorIntent,
  hasOperatorIntent,
  isBookingOriented,
  isCommissionEligibleCategory,
  normalizeTeamSize,
} from '../utils/personalization';

export interface PersonalizationContext {
  teamMemberCount: number;
  contractorCount: number;
  clientCount: number;
}

export interface SetupGuideItemConfig {
  key: string;
  section: 1 | 2 | 3;
  order: number;
  title: string;
  subtitle: string;
  estimatedMinutes: number;
  resolver: SetupGuideResolver;
  // Personalization gate. Items without a condition are always visible.
  // Principle: "show if user said this OR if the data already exists."
  condition?: (business: Business, ctx: PersonalizationContext) => boolean;
  // Milestone items are blocked until every key listed has been completed.
  blockedUntil?: string[];
  // Which surfaces render this item. Required as of §1.7.1 (Decision 2:
  // surfaces contract). No defaults, no optional — adding an item without
  // thinking about its mobile presence is a compile error, not a runtime
  // surprise. See references/SETUP_ITEMS_CONTRACT_v1.md Rule 2.
  surfaces: Array<'admin' | 'mobile'>;
}

export const SECTION_1_ITEMS: SetupGuideItemConfig[] = [
  {
    key: 'setup_wizard',
    section: 1,
    order: 0,
    title: 'Quick tax setup',
    subtitle: 'Smart defaults for HST, mileage, payment terms — based on your province and trade.',
    estimatedMinutes: 2,
    resolver: resolveSetupWizardItem,
    // Admin-only: mobile's onboarding handles bootstrap defaults via its own
    // wizard flow, not as a setup-guide item. Mirror of mobile catalog
    // (data/setupGuideCatalog.ts).
    surfaces: ['admin'],
  },
  {
    key: 'business_profile',
    section: 1,
    order: 1,
    title: 'Tell us about your business',
    subtitle: 'Name, address, phone, and a logo if you have one.',
    estimatedMinutes: 3,
    resolver: resolveBusinessProfileItem,
    surfaces: ['admin', 'mobile'],
  },
  {
    key: 'hours',
    section: 1,
    order: 2,
    title: 'Set your hours',
    subtitle: "When you're open for business.",
    estimatedMinutes: 2,
    resolver: resolveHoursItem,
    surfaces: ['admin', 'mobile'],
  },
  {
    key: 'services',
    section: 1,
    order: 3,
    title: 'List your services',
    subtitle: 'What you offer clients and what it costs.',
    estimatedMinutes: 4,
    resolver: resolveServicesItem,
    surfaces: ['admin', 'mobile'],
  },
  {
    key: 'tax',
    section: 1,
    order: 4,
    title: 'Set up tax',
    subtitle: "HST or GST, or tell us you don't charge tax yet.",
    estimatedMinutes: 2,
    resolver: resolveTaxItem,
    surfaces: ['admin', 'mobile'],
  },
  {
    key: 'invoice_branding',
    section: 1,
    order: 5,
    title: 'Customize your invoice',
    subtitle: 'What clients see when you send them an invoice.',
    estimatedMinutes: 3,
    resolver: resolveInvoiceBrandingItem,
    surfaces: ['admin', 'mobile'],
  },
];

export const SECTION_2_ITEMS: SetupGuideItemConfig[] = [
  {
    key: 'booking_page',
    section: 2,
    order: 6,
    title: 'Customize your booking page',
    subtitle: 'Your public link clients use to book.',
    estimatedMinutes: 5,
    resolver: resolveBookingPageItem,
    condition: (b) => isBookingOriented(b.onboarding_answers?.categoryId as string | null | undefined),
    surfaces: ['admin', 'mobile'],
  },
  {
    key: 'intro_offers',
    section: 2,
    order: 7,
    title: 'Set up intro offers',
    subtitle: 'A first-visit discount for new clients.',
    estimatedMinutes: 3,
    resolver: resolveIntroOffersItem,
    condition: (b) => isBookingOriented(b.onboarding_answers?.categoryId as string | null | undefined),
    surfaces: ['admin', 'mobile'],
  },
  {
    key: 'add_team',
    section: 2,
    order: 8,
    title: 'Add your team',
    subtitle: 'Invite the people you work with.',
    estimatedMinutes: 4,
    resolver: resolveAddTeamItem,
    condition: (b, ctx) => {
      const ts = normalizeTeamSize(b.onboarding_answers?.teamSize);
      return ts !== 'solo' || ctx.teamMemberCount > 0;
    },
    surfaces: ['admin', 'mobile'],
  },
  {
    key: 'add_contractors',
    section: 2,
    order: 9,
    title: 'Add your contractors',
    subtitle: 'Independent workers you pay per job.',
    estimatedMinutes: 4,
    resolver: resolveAddContractorsItem,
    condition: (b, ctx) =>
      hasContractorIntent(b.onboarding_answers?.peopleTypes) || ctx.contractorCount > 0,
    surfaces: ['admin', 'mobile'],
  },
  {
    key: 'commission_tracking',
    section: 2,
    order: 10,
    title: 'Set up commission tracking',
    subtitle: 'For staff who earn a cut of each service.',
    estimatedMinutes: 3,
    resolver: resolveCommissionTrackingItem,
    condition: (b) =>
      isCommissionEligibleCategory(b.onboarding_answers?.categoryId as string | null | undefined) &&
      hasOperatorIntent(b.onboarding_answers?.peopleTypes),
    surfaces: ['admin'], // mobile has no commission surface (§Settings.SetupGuideAnchorScroll — mirror of the mobile catalog),
  },
  {
    key: 'import_clients',
    section: 2,
    order: 11,
    title: 'Import your clients',
    subtitle: 'Bring over your existing list, or skip and add as you go.',
    estimatedMinutes: 5,
    resolver: resolveImportClientsItem,
    surfaces: ['admin', 'mobile'],
  },
];

export const SECTION_3_ITEMS: SetupGuideItemConfig[] = [
  {
    key: 'stripe_connect',
    section: 3,
    order: 13,
    title: 'Connect Stripe to get paid',
    subtitle: 'Set up online payments for invoices and bookings.',
    estimatedMinutes: 5,
    resolver: resolveStripeConnectItem,
    surfaces: ['admin', 'mobile'],
  },
  {
    key: 'send_first_invoice',
    section: 3,
    order: 14,
    title: 'Send your first invoice',
    subtitle: 'A milestone — send a real invoice to a real client.',
    estimatedMinutes: 5,
    resolver: resolveSendFirstInvoiceItem,
    blockedUntil: ['business_profile', 'services', 'tax', 'invoice_branding', 'stripe_connect'],
    surfaces: ['admin', 'mobile'],
  },
  {
    key: 'receive_first_payment',
    section: 3,
    order: 15,
    title: 'Receive your first payment',
    subtitle: "When your client pays, you're fully set up.",
    estimatedMinutes: 0,
    resolver: resolveReceiveFirstPaymentItem,
    blockedUntil: ['send_first_invoice'],
    surfaces: ['admin', 'mobile'],
  },
];

export const ALL_ITEMS: SetupGuideItemConfig[] = [
  ...SECTION_1_ITEMS,
  ...SECTION_2_ITEMS,
  ...SECTION_3_ITEMS,
];
